import type { ChangeEvent, FormEvent } from 'react'
import { styled } from '@linaria/react'
import { MinimalInput } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { SearchIcon } from '../icons'

const Style = {
  Wrap: styled.form`
    width: 100%;
    min-width: 0;
  `,
  Input: styled(MinimalInput)`
    min-height: 52px;
    border-radius: ${minimalVars.radius.pill};
  `,
}

export interface SearchFieldProps {
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  /**
   * Called with the current text when the field is submitted (Enter, or the
   * on-screen keyboard's Search key). Pages that filter as you type can ignore
   * it; pages that hand the term to another screen use it.
   */
  onSubmit?: (value: string) => void
}

/**
 * Glassy search bar for the gradient header.
 *
 * It renders a real form so Enter submits and mobile keyboards show a Search
 * key. A field wired to neither `onChange` nor `onSubmit` is a picture of a
 * search box: it accepts typing and does nothing with it, which is how the home
 * page came to have a search that did not search. Prefer omitting the field to
 * shipping one with no handler.
 */
export const SearchField = ({
  placeholder = 'Search…',
  value,
  onChange,
  onSubmit,
}: SearchFieldProps) => (
  <Style.Wrap
    role="search"
    onSubmit={(event: FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      onSubmit?.(value ?? '')
    }}
  >
    <Style.Input
      prefix={<SearchIcon size="md" />}
      type="search"
      placeholder={placeholder}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange?.(event.target.value)}
      aria-label={placeholder}
    />
  </Style.Wrap>
)
